import * as XLSX from "xlsx";

import {
  cellToString,
  findHeaderIndex,
  normalizeHeader,
  parseAmount,
  parseTxnDate,
  readBankWorkbook,
  readBankWorkbookFromText,
  sheetToAoa,
} from "./bank-statement-parse-utils";
import {
  BANK_SKIP_REASON_DEBIT,
  BankParseError,
  type BankCreditTxn,
  type BankParseResult,
  type BankSkippedRow,
} from "./bank-statement-types";

type HeaderMap = {
  txnDate: number;
  description: number;
  txnNo: number;
  withdrawal: number;
  deposit: number;
};

function detectHeaderMap(row: unknown[]): HeaderMap | null {
  const cells = row.map(normalizeHeader);
  const txnDate = findHeaderIndex(cells, ["date"]);
  const description = findHeaderIndex(cells, ["narration", "description", "particulars"]);
  const txnNo = findHeaderIndex(cells, [
    "chq./ref.no",
    "chq/ref.no",
    "chq./ref no",
    "chq/ref no",
    "chq. / ref. no",
    "ref no",
    "cheque no",
  ]);
  const withdrawal = findHeaderIndex(cells, ["withdrawal amt", "withdrawal amount", "withdrawal"]);
  const deposit = findHeaderIndex(cells, ["deposit amt", "deposit amount", "deposit"]);

  if (txnDate < 0 || withdrawal < 0 || deposit < 0) return null;
  return {
    txnDate,
    description: description >= 0 ? description : -1,
    txnNo: txnNo >= 0 ? txnNo : -1,
    withdrawal,
    deposit,
  };
}

function looksLikeHdfcTxnDate(raw: string): boolean {
  return /^\d{1,2}[/-]\d{1,2}[/-]\d{2,4}$/.test(raw.trim());
}

function isNoiseRow(row: unknown[]): boolean {
  const joined = row.map(cellToString).filter(Boolean).join(" ").toLowerCase();
  if (!joined) return true;
  if (/^\*+$/.test(joined.replace(/\s+/g, ""))) return true;
  return (
    joined.includes("statement summary") ||
    joined.includes("opening balance") ||
    joined.includes("closing bal") ||
    joined.includes("dr count") ||
    joined.includes("cr count") ||
    joined.includes("generated on") ||
    joined.includes("generated by") ||
    joined.includes("end of statement") ||
    joined.includes("registered office address") ||
    joined.includes("gstin") ||
    joined.includes("requesting branch code") ||
    joined.includes("state account branch")
  );
}

function rowLooksLikeTxn(map: HeaderMap, row: unknown[]): boolean {
  const txnDateRaw = cellToString(row[map.txnDate]);
  const withdrawalRaw = cellToString(row[map.withdrawal]);
  const depositRaw = cellToString(row[map.deposit]);
  if (!txnDateRaw && !withdrawalRaw && !depositRaw) return false;
  if (isNoiseRow(row)) return false;
  if (detectHeaderMap(row)) return false;
  if (/^\*+$/.test(txnDateRaw.replace(/\s+/g, ""))) return false;
  // HDFC txn dates are always DD/MM/YY (or similar); skip summary numeric rows.
  if (!looksLikeHdfcTxnDate(txnDateRaw)) return false;
  return true;
}

/**
 * Pure parser over sheet rows (array-of-arrays). Used by workbook/CSV entry points and tests.
 */
export function parseHdfcStatementRows(rows: unknown[][]): BankParseResult {
  let headerMap: HeaderMap | null = null;
  let headerRowNumber = 0;

  for (let i = 0; i < rows.length; i += 1) {
    const map = detectHeaderMap(rows[i] ?? []);
    if (map) {
      headerMap = map;
      headerRowNumber = i + 1;
      break;
    }
  }

  if (!headerMap) {
    throw new BankParseError(
      'Could not find an HDFC header row with "Date", "Withdrawal Amt.", and "Deposit Amt.".'
    );
  }

  const credits: BankCreditTxn[] = [];
  const skipped: BankSkippedRow[] = [];

  for (let i = headerRowNumber; i < rows.length; i += 1) {
    const row = rows[i] ?? [];
    const rowNumber = i + 1;

    if (!rowLooksLikeTxn(headerMap, row)) continue;

    const txnDateRaw = cellToString(row[headerMap.txnDate]);
    const description = headerMap.description >= 0 ? cellToString(row[headerMap.description]) : "";
    const txnNo = headerMap.txnNo >= 0 ? cellToString(row[headerMap.txnNo]) : "";
    const withdrawal = parseAmount(row[headerMap.withdrawal]);
    const deposit = parseAmount(row[headerMap.deposit]);

    if (withdrawal == null || deposit == null) {
      skipped.push({
        reason: "Invalid amount",
        rowNumber,
        txnNo: txnNo || undefined,
        txnDateRaw: txnDateRaw || undefined,
        description: description || undefined,
      });
      continue;
    }

    const txnDate = parseTxnDate(txnDateRaw);
    if (!txnDate) {
      skipped.push({
        reason: "Invalid or missing txn date",
        rowNumber,
        txnNo: txnNo || undefined,
        txnDateRaw: txnDateRaw || undefined,
        description: description || undefined,
        amount: deposit > 0 ? deposit : withdrawal > 0 ? withdrawal : undefined,
      });
      continue;
    }

    if (withdrawal > 0) {
      skipped.push({
        reason: BANK_SKIP_REASON_DEBIT,
        rowNumber,
        txnNo: txnNo || undefined,
        txnDateRaw,
        description: description || undefined,
        amount: withdrawal,
      });
      continue;
    }

    if (deposit <= 0) {
      skipped.push({
        reason: "No credit amount",
        rowNumber,
        txnNo: txnNo || undefined,
        txnDateRaw,
        description: description || undefined,
      });
      continue;
    }

    credits.push({
      txnNo,
      txnDate,
      description,
      amount: deposit,
    });
  }

  return { credits, skipped };
}

function pickHdfcSheet(wb: XLSX.WorkBook): XLSX.WorkSheet {
  const preferred = wb.SheetNames[0];
  if (!preferred) {
    throw new BankParseError("The file has no sheets.");
  }
  const sheet = wb.Sheets[preferred];
  if (!sheet) {
    throw new BankParseError(`Sheet "${preferred}" is missing.`);
  }
  return sheet;
}

/** Prefer literal DD/MM/YY strings and leading-zero ref nos (avoid SheetJS date coercion). */
const HDFC_READ_OPTS: XLSX.ParsingOptions = { cellDates: false, raw: true };

export function parseHdfcWorkbook(wb: XLSX.WorkBook): BankParseResult {
  return parseHdfcStatementRows(sheetToAoa(pickHdfcSheet(wb), { raw: true }));
}

export function parseHdfcStatementBuffer(buf: ArrayBuffer | Uint8Array): BankParseResult {
  return parseHdfcWorkbook(readBankWorkbook(buf, HDFC_READ_OPTS));
}

export function parseHdfcStatementCsvText(text: string): BankParseResult {
  return parseHdfcWorkbook(readBankWorkbookFromText(text, HDFC_READ_OPTS));
}

export async function parseHdfcStatementFile(file: File): Promise<BankParseResult> {
  const buf = await file.arrayBuffer();
  return parseHdfcStatementBuffer(buf);
}
